import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import { TextAreaField } from "@/components/ui/FormFields";
import { getEvent } from "@/api/events";
import {
  getThread,
  listThreadMessages,
  postThreadMessage,
  deleteThreadMessage,
  markThreadRead,
  type ThreadMessage,
  type ThreadStatus,
} from "@/api/threads";

const STATUS_BADGE: Record<ThreadStatus, { variant: "green" | "purple" | "gray"; label: string }> = {
  open: { variant: "green", label: "open" },
  recap: { variant: "purple", label: "recap window" },
  closed: { variant: "gray", label: "closed" },
};

/**
 * Attendees-only event thread. 403 (NOT_THREAD_MEMBER) is an expected
 * outcome for non-attendees — the Lab surfaces it instead of hiding it,
 * since testing the gate is half the point.
 */
export default function EventThreadLab() {
  const { eventId } = useParams<{ eventId: string }>();
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  // The last send, so it can be retried with the SAME key: the server must
  // answer with the same message rather than post a second one.
  const [lastSend, setLastSend] = useState<{ body: string; key: string; id: string } | null>(null);
  const [retryNote, setRetryNote] = useState<string | null>(null);

  const { data: eventData } = useQuery({
    queryKey: ["event", eventId],
    queryFn: () => getEvent(eventId!),
    enabled: !!eventId,
  });

  const {
    data: threadData,
    error: threadError,
    isLoading,
  } = useQuery({
    queryKey: ["event-thread", eventId],
    queryFn: () => getThread(eventId!),
    enabled: !!eventId,
    retry: false, // a 403 is an answer, not a flake
  });

  const thread = threadData?.data.thread;

  const { data: messagesData } = useQuery({
    queryKey: ["event-thread-messages", eventId],
    queryFn: () => listThreadMessages(eventId!),
    enabled: !!thread,
  });

  // Opening the thread counts as reading it.
  useEffect(() => {
    if (thread && eventId) {
      void markThreadRead(eventId).then(() =>
        qc.invalidateQueries({ queryKey: ["event-thread", eventId] }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread?.id]);

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["event-thread", eventId] });
    qc.invalidateQueries({ queryKey: ["event-thread-messages", eventId] });
  }

  async function send() {
    if (!eventId || !draft.trim()) return;
    setBusy(true);
    setFeedback(null);
    try {
      const key = crypto.randomUUID();
      const res = await postThreadMessage(eventId, draft.trim(), key);
      setLastSend({ body: draft.trim(), key, id: res.data.id });
      setRetryNote(null);
      setDraft("");
      invalidate();
    } catch (e) {
      setFeedback(e instanceof Error ? e.message : "Post failed");
    } finally {
      setBusy(false);
    }
  }

  async function retryLastSend() {
    if (!eventId || !lastSend) return;
    setBusy(true);
    setFeedback(null);
    try {
      const res = await postThreadMessage(eventId, lastSend.body, lastSend.key);
      setRetryNote(
        res.data.id === lastSend.id
          ? "Same key, same message: nothing new was posted."
          : `A second message was posted (${res.data.id}). That is the bug the key exists to stop.`,
      );
      invalidate();
    } catch (e) {
      setFeedback(e instanceof Error ? e.message : "Retry failed");
    } finally {
      setBusy(false);
    }
  }

  async function remove(messageId: string) {
    if (!eventId) return;
    setBusy(true);
    setFeedback(null);
    try {
      await deleteThreadMessage(eventId, messageId);
      invalidate();
    } catch (e) {
      setFeedback(e instanceof Error ? e.message : "Delete failed");
    } finally {
      setBusy(false);
    }
  }

  const eventTitle = eventData?.data?.title ?? "Event";
  // API returns newest-first; render oldest-first for a chat feel.
  const messages = [...(messagesData?.data.messages ?? [])].reverse();

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Thread — ${eventTitle}`}
        subtitle="Attendees-only coordination chat. Logistics evaporate after the event; recap posts survive."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Events", href: "/lab/events" },
          { label: "Thread" },
        ]}
      />

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : threadError ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800">
          {threadError instanceof Error
            ? threadError.message
            : "Could not open the thread"}{" "}
          — this thread is attendees-only (RSVP “going”, hold a ticket, or be
          the organizer).
        </div>
      ) : thread ? (
        <>
          <div className="flex items-center gap-2">
            <Badge variant={STATUS_BADGE[thread.status].variant}>
              {STATUS_BADGE[thread.status].label}
            </Badge>
            {thread.status === "recap" && (
              <span className="text-xs text-gray-500">
                posts made now become the permanent recap
              </span>
            )}
            {thread.status === "closed" && (
              <span className="text-xs text-gray-500">
                chat has evaporated — recap below is what remains
              </span>
            )}
          </div>

          <ul className="space-y-2">
            {messages.length === 0 && (
              <li className="text-sm text-gray-500">No messages yet.</li>
            )}
            {messages.map((m) => (
              <MessageRow key={m.id} m={m} busy={busy} onDelete={remove} />
            ))}
          </ul>

          {thread.status !== "closed" && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
              <TextAreaField
                label={
                  thread.status === "recap"
                    ? "Add to the recap (survives forever)"
                    : "Message the attendees"
                }
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={2}
                maxLength={2000}
              />
              <div className="flex items-center gap-3">
                <Button size="sm" onClick={send} disabled={busy || !draft.trim()}>
                  {thread.status === "recap" ? "Post recap" : "Send"}
                </Button>
                {lastSend && (
                  <Button size="sm" variant="secondary" onClick={retryLastSend} disabled={busy}>
                    Retry the last send with its key
                  </Button>
                )}
                {retryNote && <span className="text-xs text-gray-600">{retryNote}</span>}
                {feedback && (
                  <span className="text-xs text-red-600">{feedback}</span>
                )}
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

function MessageRow({
  m,
  busy,
  onDelete,
}: {
  m: ThreadMessage;
  busy: boolean;
  onDelete: (id: string) => void;
}) {
  const time = new Date(m.createdAt).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  if (m.kind === "nudge") {
    return (
      <li className="text-center text-xs text-gray-500 italic py-1">
        {m.body}
      </li>
    );
  }

  return (
    <li
      className={`bg-white border rounded-xl px-4 py-2 ${
        m.kind === "recap" ? "border-purple-200 bg-purple-50" : "border-gray-200"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-gray-500">
          <span className="font-medium text-gray-700">
            {m.user?.name ?? "System"}
          </span>
          {" · "}
          {time}
          {m.kind === "recap" && (
            <>
              {" · "}
              <Badge variant="purple">recap</Badge>
            </>
          )}
        </div>
        {m.isOwn && (
          <Button size="sm" variant="ghost" onClick={() => onDelete(m.id)} disabled={busy}>
            Delete
          </Button>
        )}
      </div>
      <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">{m.body}</p>
    </li>
  );
}
